//@flow
import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { withRouter } from 'common/components/core/router';
import * as Immutable from 'immutable';
import { translate } from 'react-i18next';

import { EnhancedField } from 'common/components/core/form/Field';
import { EntityModal } from 'common/entity/generic/EntityModal';
import { MultiTeamSelect } from 'common/entity/components/team';
import { HelpTooltip } from 'common/components/core/help';

import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';

import { Skill } from 'common/models/internal/Skill';
import { TeamId } from 'common/models/internal/TeamId';
import { ApiError } from 'common/modules/api';

import SkillActions from 'common/redux/actions/skill';

import type { Team } from 'common/models/internal/Team';
import type { List, Map } from 'immutable';

type Props = {
    theme: Object,
    clientId: string,
    teams: Map<string, Team>,
    defaultSelectedTeams?: List<TeamId>,
    onDismiss: (?boolean) => void,
    addSkill: ({ clientId: string, skillId: string }, Skill) => Promise<*>
};

type State = {
    skill: Skill,
    errorMessage?: ?string,
    isSaving?: boolean
};

class MiniSkillMasterEditor extends React.Component<Props, State> {
    constructor(props) {
        super(props);
        let defaultSkill = Skill.fromJS({
            teams: null
        });
        const { defaultSelectedTeams } = props;
        if (defaultSelectedTeams) {
            defaultSkill = defaultSkill.setTeams(defaultSelectedTeams);
        }
        this.state = {
            skill: defaultSkill
        };
    }

    handleIdChange = (value: string) => {
        this.setState(state => {
            const { skill } = state;
            return {
                skill: skill.setId(value)
            };
        });
    };

    handleNameChange = (value: string) => {
        this.setState(state => {
            const { skill } = state;
            return {
                skill: skill.setName(value)
            };
        });
    };

    onTeamSelect = selectedOptions => {
        const { clientId } = this.props;
        const { skill } = this.state;
        const teamIds = selectedOptions.map(option => {
            return TeamId.fromJS({
                clientId,
                teamId: option.value
            });
        });
        this.setState({
            skill: skill.setTeams(Immutable.List(teamIds))
        });
    };

    clearErrorMessage = () => {
        // Clear message after 5 sec
        setTimeout(() => {
            this.setState({
                errorMessage: null
            });
        }, 5000);
    };

    handleSkillAdd = () => {
        const { clientId, addSkill, onDismiss } = this.props;
        const { skill } = this.state;
        const updatedSkill = skill.setAllValidationErrors();
        this.setState({ skill: updatedSkill });
        if (updatedSkill.hasValidationErrors()) {
            this.setState(
                {
                    errorMessage:
                        'Invalid or Incomplete details, please check above.'
                },
                this.clearErrorMessage
            );
            return;
        }
        this.setState({ isSaving: true });
        addSkill(
            {
                clientId,
                skillId: skill.id
            },
            skill
        )
            .then(res => onDismiss(res))
            .catch(error => {
                if (error instanceof ApiError) {
                    this.setState({
                        isSaving: false,
                        errorMessage: error.message
                    });
                    return;
                }
                throw error;
            });
    };

    render() {
        const { className, theme, teams, onDismiss, t } = this.props;
        const { skill, isSaving, errorMessage } = this.state;
        return (
            <EntityModal
                theme={theme}
                title={t('miniSkillsMaster.addSkill')}
                className={classnames(className, theme.skillMasterModalOverlay)}
                onDismiss={onDismiss}
                acceptErrorMessage={errorMessage}
                isLoading={isSaving}
                acceptActionLabel={'Add'}
                onAccept={this.handleSkillAdd}
            >
                <div className={theme.fieldGroup}>
                    <span className={theme.label}>ID*</span>
                    <EnhancedField
                        type="text"
                        element="input"
                        value={skill.id}
                        placeholder={t('miniSkillsMaster.idPlaceholder')}
                        onChange={this.handleIdChange}
                        theme={theme}
                        error={skill.getValidationMessage('id')}
                    />
                </div>
                <div className={theme.fieldGroup}>
                    <span className={theme.label}>Name*</span>
                    <EnhancedField
                        type="text"
                        element="input"
                        value={skill.name}
                        placeholder={t('miniSkillsMaster.namePlaceholder')}
                        onChange={this.handleNameChange}
                        theme={theme}
                        error={skill.getValidationMessage('name')}
                    />
                </div>
                <div className={theme.fieldGroup}>
                    <span className={theme.label}>
                        <span>{t('miniSkillsMaster.teams')}</span>
                        <HelpTooltip
                            tooltipText={t('miniSkillsMaster.teamsHelp')}
                        />
                    </span>
                    <MultiTeamSelect
                        teams={teams}
                        onChange={this.onTeamSelect}
                        value={skill.teams}
                    />
                </div>
            </EntityModal>
        );
    }
}

const mapStateToProps = (state, props) => {
    const clientId = clientSelector(state, props);
    const teams = teamsSelector(state, props);

    return {
        clientId,
        teams
    };
};

const actions = {
    addSkill: SkillActions.addEntity
};

export default withRouter(
    connect(
        mapStateToProps,
        actions
    )(translate('entityManagement')(MiniSkillMasterEditor))
);
